import PopUpBase from '@components/PopUpBase';
import { DnMode } from '@EcamModel/model';
import { DaySection } from '@EcamModel/model/Solar4gDaySection';
import { IPopUp } from '@hooks/usePopUp';
import { Button, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import useMileSightSolar from '@pages/cameras/hook/useMilesightSolar';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { SliderControl } from './PopUpEditImageAdjustment';
import useMedia from '@hooks/useMedia';
type Props = Omit<IPopUp, 'onConfirm'> & {
    onConfirm?(data: FormValues): void;
};

type FormValues = {
    DnMode: DnMode;
    DayToNight: number;
    NightToDay: number;
    IrStrength: number;
    DaySecions?: DaySection[];
};

const initialDaySections: DaySection[] = [
    {
        month: 0,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 1,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 2,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 3,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 4,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 5,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 6,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 7,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 8,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 9,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 10,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
    {
        month: 11,
        hourStart: 7,
        minStart: 0,
        hourStop: 19,
        minStop: 0,
    },
];

export default function PopUpEditDayNightSwitch(props: Props) {
    const { Solar4gImageInfo, Solar4gDaySection } = useMileSightSolar();
    const { isMediumMD, isMobileSM } = useMedia()

    const [selectedMonth, setSelectedMonth] = useState<Moment>(moment());

    const [startTime, setStartTime] = useState<Moment | null>(null);

    const [endTime, setEndTime] = useState<Moment | null>(null);


    const {
        control,
        setValue,
        reset,
        handleSubmit,
        watch,
        formState: { isDirty, isValid, errors },
    } = useForm<FormValues>({
        mode: 'all',
        defaultValues: {},
    });

    const dnMode = watch().DnMode;

    const daySections = watch().DaySecions;

    const disabled = !(isValid && isDirty);

    const slidersForm: SliderControl<FormValues>[] = [
        {
            label: 'Day to Night',
            name: 'DayToNight',
        },
        {
            label: 'Night to Day',
            name: 'NightToDay',
        },
        {
            label: 'IR LED level',
            name: 'IrStrength',
        },
    ];

    const handleMonthChange = (newDate: moment.Moment | null) => {
        if (newDate) {
            setSelectedMonth(newDate);
        }
    };
    const handleStartTimeChange = (newTime: Moment | null) => {
        setStartTime(newTime);
        const updatedDaySections = daySections?.map((item) =>
            item.month === selectedMonth.month()
                ? {
                    ...item,
                    hourStart: moment(newTime).hour(),
                    minStart: moment(newTime).minute(),
                }
                : item
        );
        setValue('DaySecions', updatedDaySections, { shouldDirty: true });
    };

    const handleEndTimeChange = (newTime: Moment | null) => {
        setEndTime(newTime);
        const updatedDaySections = daySections?.map((item) =>
            item.month === selectedMonth.month()
                ? {
                    ...item,
                    hourStop: moment(newTime).hour(),
                    minStop: moment(newTime).minute(),
                }
                : item
        );
        setValue('DaySecions', updatedDaySections, { shouldDirty: true });
    };

    useEffect(() => {
        if (!props.open) return;

        reset({
            DnMode: Solar4gImageInfo?.DnMode,
            DayToNight: Solar4gImageInfo?.DayToNight,
            NightToDay: Solar4gImageInfo?.NightToDay,
            IrStrength: Solar4gImageInfo?.IrStrength,
            DaySecions: Solar4gDaySection?.DaySection,
        });

        setSelectedMonth(moment());
    }, [props.open]);

    useEffect(() => {
        if (Number(dnMode) !== DnMode.Schedule) return;

        const month = selectedMonth.month();

        const schedule = daySections?.find((item) => item.month === month);

        if (schedule) {
            setStartTime(moment().hour(schedule.hourStart).minute(schedule.minStart));
            setEndTime(moment().hour(schedule.hourStop).minute(schedule.minStop));
        }
    }, [dnMode, daySections, selectedMonth]);

    return (
        <PopUpBase
            open={props.open}
            onClose={props.onClose}
            onConfirm={() => { }}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: '600px',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            title={<Typography variant="h4">Day/Night switch</Typography>}
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            desc={
                <Stack width="100%" sx={{ background: '#FAFAFA', p: 2, mt: 2 }} spacing={3}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography>Mode</Typography>
                        <Controller
                            name="DnMode"
                            control={control}
                            render={({ field, fieldState: { error } }) => {
                                return (
                                    <RadioGroup
                                        {...field}
                                        value={field.value}
                                        onChange={(e) => field.onChange(e)}
                                        sx={{ width: '69%' }}
                                    >
                                        <Stack direction={isMediumMD ? "column" : "row"}>
                                            {[
                                                { label: 'Night', value: DnMode.Night },
                                                { label: 'Day', value: DnMode.Day },
                                                { label: 'Auto', value: DnMode.Auto },
                                                { label: 'Schedule', value: DnMode.Schedule },
                                            ].map((item) => {
                                                return (
                                                    <FormControlLabel
                                                        value={item.value}
                                                        control={<Radio />}
                                                        label={item.label}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </RadioGroup>
                                );
                            }}
                        />
                    </Stack>
                    {Number(dnMode) === DnMode.Schedule && (
                        <>
                            <DatePicker
                                label="Month"
                                views={['month']}
                                openTo="month"
                                value={selectedMonth}
                                onChange={handleMonthChange}
                            />
                            <Stack direction="row" justifyContent="space-between" spacing={2}>
                                <TimePicker
                                    label="Start time of day"
                                    ampm={false}
                                    value={startTime}
                                    onChange={handleStartTimeChange}
                                    sx={{ width: '100%' }}
                                />
                                <TimePicker
                                    label="End time of day"
                                    ampm={false}
                                    value={endTime}
                                    minTime={moment(startTime).add(5, 'minutes') || undefined}
                                    onChange={handleEndTimeChange}
                                    sx={{ width: '100%' }}
                                />
                            </Stack>
                        </>
                    )}

                    {slidersForm.map((item) => (
                        <SliderControl key={item.name} control={control} label={item.label} name={item.name} />
                    ))}
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="cancel"
                        onClick={() => {
                            props.onClose?.();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={disabled}
                        onClick={handleSubmit((data) => {
                            const _data: FormValues = {
                                ...data,
                                DnMode: Number(data.DnMode),
                            };
                            props.onConfirm?.(_data);
                        })}
                    >
                        Save
                    </Button>
                </Stack>
            }
        />
    );
}
